import type { HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * The UAE Design System 3.0 badge (`aegov-badge`): 4px corners, a semibold label, a soft fill in the palette's 50 swatch with text in
 * its 700 (`solid` is the 600 fill with white text). Variants follow the Design System's: gold (the default badge), success (AE Green),
 * info (Tech Blue), warning (Camel), danger (AE Red); `default` and `outline` are neutral AE Black badges, `ai` marks AI output in
 * the AI gold (a gold wash and edge).
 */
export const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-sm border border-transparent px-2 py-0.5 text-xs leading-4 font-semibold whitespace-nowrap transition-colors [&_svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-aeblack-100/70 text-aeblack-800",
        gold: "bg-aegold-50 text-aegold-700",
        solid: "bg-aegold-600 text-white",
        outline: "border-aeblack-100 bg-white text-aeblack-800",
        success: "bg-aegreen-50 text-aegreen-700",
        warning: "bg-camel-50 text-camel-700",
        danger: "bg-aered-50 text-aered-700",
        info: "bg-techblue-50 text-techblue-700",
        camel: "bg-camel-100 text-camel-800",
        ai: "border-aegold-200 bg-linear-to-r from-aegold-50 to-aegold-100 text-aegold-800",
      },
      size: {
        default: "",
        sm: "px-1.5 py-0 text-[10px] leading-4",
        lg: "px-3 py-1 text-sm leading-6",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  dot?: boolean;
  pulse?: boolean;
}

export function Badge({ className, variant, size, dot, pulse, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot ? <span className={cn("size-1.5 rounded-full bg-current", pulse && "animate-pulse")} /> : null}
      {children}
    </span>
  );
}

const statusMap: Record<string, BadgeProps["variant"]> = {
  Draft: "default",
  Submitted: "info",
  UnderReview: "gold",
  AwaitingApplicant: "warning",
  Accepted: "success",
  Published: "info",
  Investigation: "camel",
  Decision: "gold",
  Decided: "success",
  Rejected: "danger",
  Closed: "default",
  Suspended: "warning",
  Withdrawn: "default",
  OnTrack: "success",
  AtRisk: "warning",
  Breached: "danger",
  Completed: "default",
  Paused: "default",
  Proposed: "ai",
  Approved: "success",
  Edited: "success",
  Superseded: "default",
  Expired: "default",
  Low: "default",
  Medium: "info",
  High: "warning",
  Critical: "danger",
  Healthy: "success",
  Degraded: "warning",
  Down: "danger",
  Unknown: "default",
  Pending: "warning",
  Processing: "info",
  Failed: "danger",
  Paid: "success",
  Sent: "success",
  Delivered: "success",
  Admissible: "success",
  Inadmissible: "danger",
  Upheld: "danger",
  Dismissed: "default",
};

export function StatusBadge({ status, label, className, ...props }: { status: string; label?: string } & Omit<BadgeProps, "variant">) {
  return (
    <Badge variant={statusMap[status] ?? "default"} dot className={className} {...props}>
      {label ?? status}
    </Badge>
  );
}
